import React, { useState, useEffect, useCallback } from 'react';
import { useDarkMode } from 'shared/context/DarkModeContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import API from 'shared/api';
import {
  FiShoppingBag,
  FiSearch,
  FiRefreshCw,
  FiX,
  FiFileText,
} from 'react-icons/fi';

const money = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ESTADO_STYLE = {
  COBRADA: { bg: '#e6f4ea', fg: '#1e7e34' },
  'POR COBRAR': { bg: '#fdecea', fg: '#b42318' },
  ABONADA: { bg: '#fef3e2', fg: '#b45309' },
  anulada: { bg: '#f1f3f5', fg: '#6B7280' },
};

function EstadoBadge({ estado }) {
  const st = ESTADO_STYLE[estado] || { bg: '#eef2ff', fg: '#3730a3' };
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        background: st.bg,
        color: st.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {estado || '—'}
    </span>
  );
}

function DetalleModal({ venta, onClose, COL }) {
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setDetalle(null);
    setError(false);
    API.get(`/ventas/${venta.id}`)
      .then((r) => {
        if (alive && r.data.status === 'success') setDetalle(r.data.data);
      })
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [venta.id]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1500,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COL.white,
          borderRadius: 14,
          width: '100%',
          maxWidth: 620,
          maxHeight: '84vh',
          overflowY: 'auto',
          boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '14px 18px',
            borderBottom: `1px solid ${COL.border}`,
            position: 'sticky',
            top: 0,
            background: COL.white,
            zIndex: 1,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, color: COL.text }}>
            {detalle?.factura ? `Factura #${detalle.factura}` : `Venta #${venta.id}`}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: COL.muted, fontSize: 18 }}
          >
            <FiX />
          </button>
        </div>

        <div style={{ padding: '14px 18px' }}>
          {error && <div style={{ color: COL.red, fontSize: 13 }}>No se pudo cargar el detalle.</div>}
          {!detalle && !error && <div style={{ color: COL.muted, fontSize: 13 }}>Cargando…</div>}
          {detalle && (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12, fontSize: 12, color: COL.muted }}>
                <span style={{ fontWeight: 700, color: COL.text }}>
                  {money(detalle.total)}
                </span>
                <EstadoBadge estado={detalle.estado || (detalle.anulada ? 'anulada' : '')} />
                <span>{detalle.sede_codigo}</span>
                <span>{(detalle.metodo_pago || '—').replace(/_/g, ' ')}</span>
                <span>{detalle.fecha ? detalle.fecha.slice(0, 16).replace('T', ' ') : ''}</span>
              </div>

              {detalle.cliente_nombre && (
                <div style={{ fontSize: 13, color: COL.text, marginBottom: 6 }}>
                  Cliente: <strong>{detalle.cliente_nombre}</strong>
                  {detalle.cliente_cedula ? ` · ${detalle.cliente_cedula}` : ''}
                </div>
              )}
              {detalle.saldo_usd != null && detalle.saldo_usd > 0 && (
                <div style={{ fontSize: 12, color: COL.amber, marginBottom: 6 }}>
                  Saldo pendiente: {money(detalle.saldo_usd)}
                  {detalle.fecvenci ? ` · vence ${detalle.fecvenci}` : ''}
                </div>
              )}

              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    {['Producto', 'Cant.', 'Precio', 'Subtotal'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '6px 8px', color: COL.muted, borderBottom: `1px solid ${COL.border}`, fontWeight: 600 }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(detalle.items || []).map((it, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${COL.border}` }}>
                      <td style={{ padding: '6px 8px', color: COL.text }}>
                        <div style={{ fontWeight: 600 }}>{it.nombre_producto}</div>
                        <div style={{ color: COL.muted, fontSize: 10 }}>{it.codigo_producto}</div>
                      </td>
                      <td style={{ padding: '6px 8px', color: COL.text }}>{it.cantidad}</td>
                      <td style={{ padding: '6px 8px', color: COL.muted }}>{money(it.precio_unitario)}</td>
                      <td style={{ padding: '6px 8px', color: COL.text, fontWeight: 600 }}>{money(it.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginTop: 12, fontSize: 12, color: COL.muted }}>
                <span>Subtotal: <strong style={{ color: COL.text }}>{money(detalle.subtotal)}</strong></span>
                <span>Impuesto: <strong style={{ color: COL.text }}>{money(detalle.impuesto)}</strong></span>
                <span>Total: <strong style={{ color: COL.text }}>{money(detalle.total)}</strong></span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Ventas() {
  const { COL } = useDarkMode();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [ventas, setVentas] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [ultimoId, setUltimoId] = useState(null);
  const [q, setQ] = useState('');
  const [estado, setEstado] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [cargando, setCargando] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const cargar = useCallback(
    async (reset) => {
      setCargando(true);
      try {
        const p = new URLSearchParams();
        p.set('limite', '50');
        if (q.trim()) p.set('q', q.trim());
        if (estado) p.set('estado', estado);
        if (fechaDesde) p.set('fecha_desde', fechaDesde);
        if (fechaHasta) p.set('fecha_hasta', fechaHasta);
        if (!reset && ultimoId) p.set('ultimo_id', ultimoId);
        const r = await API.get(`/ventas/historial?${p.toString()}`);
        if (r.data.status === 'success') {
          const d = r.data.data;
          setVentas((prev) => (reset ? d.ventas : [...prev, ...d.ventas]));
          setHasMore(d.has_more);
          if (d.has_more && d.ventas.length) setUltimoId(d.ventas[d.ventas.length - 1].id);
        }
      } catch (e) {
        console.error('Error cargando historial de ventas', e);
      } finally {
        setCargando(false);
      }
    },
    [q, estado, fechaDesde, fechaHasta, ultimoId]
  );

  useEffect(() => {
    cargar(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, estado, fechaDesde, fechaHasta]);

  const buscar = (e) => {
    e.preventDefault();
    cargar(true);
  };

  return (
    <div style={{ padding: isMobile ? 12 : 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiShoppingBag size={20} color={COL.pri} />
          <h2 style={{ fontSize: 18, fontWeight: 700, color: COL.text, margin: 0 }}>Historial de ventas</h2>
        </div>
        <button
          onClick={() => cargar(true)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 12px',
            borderRadius: 8,
            border: `1px solid ${COL.border}`,
            background: COL.white,
            color: COL.text,
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          <FiRefreshCw size={13} /> Actualizar
        </button>
      </div>

      <form onSubmit={buscar} style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <FiSearch size={14} color={COL.muted} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar factura, cliente o RIF…"
            style={{
              width: '100%',
              padding: '8px 10px 8px 30px',
              borderRadius: 8,
              border: `1px solid ${COL.border}`,
              background: COL.white,
              color: COL.text,
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>
        <select value={estado} onChange={(e) => setEstado(e.target.value)} style={{ padding: '8px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.white, color: COL.text, fontSize: 13 }}>
          <option value="">Todos los estados</option>
          <option value="COBRADA">Cobrada</option>
          <option value="POR COBRAR">Por cobrar</option>
          <option value="ABONADA">Abonada</option>
        </select>
        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={{ padding: '7px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.white, color: COL.text, fontSize: 12 }} />
        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={{ padding: '7px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.white, color: COL.text, fontSize: 12 }} />
      </form>

      <div style={{ background: COL.white, borderRadius: 12, border: `1px solid ${COL.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: COL.bg }}>
              {['Factura', 'Fecha', 'Cliente', 'Estado', 'Items', 'Total', 'Saldo'].map((h) => (
                <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: COL.muted, fontWeight: 600, borderBottom: `1px solid ${COL.border}` }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ventas.map((vt) => (
              <tr key={vt.id} onClick={() => setDetalle(vt)} style={{ cursor: 'pointer', borderBottom: `1px solid ${COL.border}` }}>
                <td style={{ padding: '10px 12px', color: COL.text }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                    <FiFileText size={13} color={COL.muted} />
                    {vt.factura ? `#${vt.factura}` : `#${vt.ticket_numero || vt.id}`}
                  </div>
                  {vt.origen === 'FAC' && <div style={{ fontSize: 9, color: COL.muted }}>Facturada fiscal</div>}
                  {vt.origen === 'ESP' && <div style={{ fontSize: 9, color: COL.muted }}>En espera</div>}
                </td>
                <td style={{ padding: '10px 12px', color: COL.muted, whiteSpace: 'nowrap' }}>{vt.fecha ? vt.fecha.slice(0, 10) : ''}</td>
                <td style={{ padding: '10px 12px', color: COL.text }}>{vt.cliente_nombre || '—'}</td>
                <td style={{ padding: '10px 12px' }}><EstadoBadge estado={vt.estado} /></td>
                <td style={{ padding: '10px 12px', color: COL.muted }}>{vt.total_items}</td>
                <td style={{ padding: '10px 12px', color: COL.green, fontWeight: 700 }}>{money(vt.total)}</td>
                <td style={{ padding: '10px 12px', color: vt.saldo_usd > 0 ? COL.amber : COL.muted, fontWeight: vt.saldo_usd > 0 ? 600 : 400 }}>
                  {vt.saldo_usd > 0 ? money(vt.saldo_usd) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {ventas.length === 0 && !cargando && (
          <div style={{ textAlign: 'center', padding: 30, color: COL.muted, fontSize: 13 }}>Sin ventas para el filtro seleccionado</div>
        )}
        {cargando && ventas.length === 0 && (
          <div style={{ textAlign: 'center', padding: 30, color: COL.muted, fontSize: 13 }}>Cargando…</div>
        )}
        {hasMore && (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <button
              onClick={() => cargar(false)}
              disabled={cargando}
              style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.white, color: COL.text, cursor: 'pointer', fontSize: 12 }}
            >
              {cargando ? 'Cargando…' : 'Cargar más'}
            </button>
          </div>
        )}
      </div>

      {detalle && <DetalleModal venta={detalle} onClose={() => setDetalle(null)} COL={COL} />}
    </div>
  );
}
